import React from 'react';
import { cn } from '@components/ui/utils';

/**
 * Sliding pill switch for boolean form fields. Wraps a native
 * <input type="checkbox"> so it's accessible and form-libraries can
 * read/write it as a normal checkbox.
 *
 * Usage: <Switch checked={x} onChange={setX} label="Wingman" />
 */
const Switch = ({
  checked = false,
  onChange,
  disabled = false,
  label,
  description,
  className,
  size = 'md',
}) => {
  const dims = size === 'sm'
    ? { track: 'w-8 h-4', knob: 'w-3 h-3', on: 'translate-x-4', off: 'translate-x-0.5' }
    : { track: 'w-10 h-5', knob: 'w-4 h-4', on: 'translate-x-5', off: 'translate-x-0.5' };
  const control = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={cn(
        'relative inline-flex flex-shrink-0 items-center rounded-full transition-colors',
        dims.track,
        checked ? 'bg-primary-600' : 'bg-gray-300',
        disabled && 'opacity-50 cursor-not-allowed',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
      )}
    >
      <span
        className={cn(
          'inline-block rounded-full bg-white shadow transform transition-transform',
          dims.knob,
          checked ? dims.on : dims.off,
        )}
      />
    </button>
  );

  if (!label && !description) return <span className={className}>{control}</span>;

  return (
    <label
      className={cn(
        'flex items-center justify-between gap-3 py-2 cursor-pointer',
        disabled && 'cursor-not-allowed opacity-70',
        className,
      )}
    >
      <div className="flex-1 min-w-0">
        {label && <div className="text-sm font-medium text-gray-800">{label}</div>}
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      {control}
    </label>
  );
};

export default Switch;
